import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { ListItemUnitRequest } from "../../../../../services/soap/item/requests/ListItemUnitRequest";

interface ItemUnit {
  id: string;
  name: string;
  treeHandle: string;
}

interface IItemUnitsDropDown {
  selectedUnit: string;
  onChange: (unit: ItemUnit | null) => void; // ✅ allow null when cleared
  isEdit: any;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
}

const ItemUnitsDropDown: React.FC<IItemUnitsDropDown> = ({
  selectedUnit,
  onChange,
  isEdit,
  isRequired,
  errorMessage,
  validationState,
}) => {
  const { t } = useTranslation("products");
  const _t = (key: string) => t(`addNewProduct.${key}`, { ns: "products" });

  const { store, itemService } = useDependency();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const didInitialAutoSelectRef = useRef(false);
  const isUserEditingRef = useRef(false);

  const list = useAsyncList<ItemUnit>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 50;

      try {
        const response = await itemService.listItemUnit(
          new ListItemUnitRequest(store.Server, store.SessionId)
        );

        let items: ItemUnit[] = response?.ITEM_UNIT || [];
        if (!Array.isArray(items)) return { items: [] };

        // ✅ search filtering by name
        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((x) => (x.name ?? "").toLowerCase().includes(ft));
        }

        return {
          items,
          cursor: items.length === limit ? String(offset + limit) : undefined,
        };
      } catch (error) {
        console.error("Error fetching units:", error);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ initial load: auto-select 0th item ONCE (only when not edit)
  useEffect(() => {
    if (!didInitialAutoSelectRef.current && list.items.length > 0 && !isEdit) {
      didInitialAutoSelectRef.current = true;

      const first = list.items[0];
      setSelectedKey(String(first.id));
      setInputValue(first.name ?? "");
      onChange(first);
    }
  }, [list.items, isEdit, onChange]);

  // ✅ sync from parent ONLY when user not typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedUnit) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedUnit));
    const selected = list.items.find((x) => String(x.id) === String(selectedUnit));
    setInputValue(selected?.name ?? "");
  }, [selectedUnit, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // ✅ allow backspace: if user text diverges from selected label, clear selection
    if (selectedKey) {
      const selected = list.items.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // ✅ cleared => empty dropdown
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? ""); // ✅ immediate display
      onChange(selected);
      list.setFilterText(""); // ✅ next focus shows all values
    }
  };

  return (
    <ComboBox
      label={_t("units")}
      aria-label={_t("units")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      width={{ base: "100%", L: "50%" }}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      isDisabled={isEdit}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default ItemUnitsDropDown;
